<template>
  <div class="mt-3 pt-3 border-t border-gray-200 space-y-3">
    <!-- Source / addresses block -->
    <div class="space-y-1 text-sm">
      <div class="text-[#0e141b]">
        <span class="text-[#4e7397]">Source:</span>
        {{ swap.source_amount }} {{ swap.source_token_symbol }}
        <span v-if="swap.source_amount_usd" class="text-[#4e7397]">
          ({{ swap.source_amount_usd }})
        </span>
      </div>
      <div v-if="sourceAddress" class="text-[#0e141b]">
        <span class="text-[#4e7397]">From:</span>
        {{ formatAddressWithChain(sourceAddress, sourceChainType, swap.source_chain_id) }}
      </div>
      <div v-if="destinationAddress" class="text-[#0e141b]">
        <span class="text-[#4e7397]">To:</span>
        {{ formatAddressWithChain(destinationAddress, 'ton', null) }}
      </div>
    </div>

    <!-- Steps -->
    <div>
      <div class="text-xs uppercase tracking-wide text-[#4e7397] mb-2">Steps</div>
      <div v-if="steps.length === 0" class="text-sm text-[#4e7397]">
        No on-chain transactions recorded yet.
      </div>
      <ol v-else class="space-y-3">
        <li v-for="(tx, idx) in steps" :key="tx.id || `tx-${idx}`" class="flex items-start gap-3">
          <RecentSwapStepIcon :kind="stepIconKind(tx, swap.status)" class="flex-shrink-0 mt-0.5" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-medium text-[#0e141b] truncate">
                {{ stepLabel(tx, swap) }}
              </span>
              <span class="text-xs text-[#4e7397] flex-shrink-0">
                {{ stepDurationLabel(tx) }}
              </span>
            </div>
            <div class="text-xs text-[#4e7397] mt-0.5">
              {{ formatExpectedVsActual(tx) }}
            </div>
            <div v-if="tx.tx_hash" class="flex items-center gap-1.5 mt-1">
              <span class="text-xs text-[#4e7397]">{{ txHashLabel(tx) }}:</span>
              <a
                :href="txExplorerUrl(tx)"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="`View transaction on ${txChainLabel(tx)} explorer`"
                class="text-xs text-[#1980e6] hover:text-[#4aa3ff] transition-colors inline-flex items-center gap-1 min-w-0"
              >
                <span class="truncate">{{ truncateAddress(tx.tx_hash, 8, 6) }}</span>
                <svg
                  class="w-3 h-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </li>
      </ol>
    </div>

    <!-- Failed-swap error banner -->
    <div
      v-if="swap.status === 'failed'"
      class="text-xs text-[#b42318] bg-[#b42318]/10 rounded-lg px-3 py-2"
    >
      {{ failedMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import RecentSwapStepIcon from './RecentSwapStepIcon.vue'
import {
  formatAddressWithChain,
  presentSwapSteps,
  formatExpectedVsActual,
  stepDurationLabel,
  stepIconKind,
  stepLabel,
  truncateAddress,
  txChainLabel,
  txExplorerUrl,
  type RecentSwap,
  type RecentSwapStepView,
} from './presentation'

const props = defineProps<{ swap: RecentSwap }>()
const steps = computed(() => presentSwapSteps(props.swap))

const sourceAddress = computed(() => props.swap.evm_address)
const sourceChainType = computed(() => props.swap.source_chain_type)
const destinationAddress = computed(() => props.swap.destination_address)

/**
 * Per-step hash label. Bridge txs on the source chain are the deposit
 * point that the user should recognise — label them "Source tx". Anything
 * else (TON-side bridge ack, DEX swaps) gets the generic "Tx".
 */
const txHashLabel = (tx: RecentSwapStepView): string => {
  if (tx.tx_type === 'bridge' && tx.chain_type !== 'ton') return 'Source tx'
  return 'Tx'
}

// Generic copy for v1. Mapping specific lastError enums to friendly
// strings is out-of-scope per PHASE-6-FRONTEND-PLAN.md §9.
const failedMessage = 'Swap failed. Please try again.'
</script>
